import { useEffect, useState } from "react";
import { KeyRound, Save, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AssignmentFileLink } from "@/components/AssignmentFileLink";
import { ASSIGNMENT_FILE_ACCEPT, assignmentFileError, uploadAssignmentFiles } from "@/lib/assignmentFiles";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const fields = ["title", "subject", "description", "due_date", "video_url", "answer_key_text"];

export const AssignmentEditDialog = ({ assignment, subjects, onClose, onSaved }) => {
  const [form, setForm] = useState({});
  const [attachments, setAttachments] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [answerKeyFile, setAnswerKeyFile] = useState(null);
  const [removedFileIds, setRemovedFileIds] = useState([]);
  const [removeAnswerKey, setRemoveAnswerKey] = useState(false);
  const [legacyChanged, setLegacyChanged] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!assignment) return;
    setForm(Object.fromEntries(fields.map((field) => [field, assignment[field] ?? ""])));
    setAttachments(assignment.attachments || []);
    setNewFiles([]); setAnswerKeyFile(null); setRemovedFileIds([]);
    setRemoveAnswerKey(false); setLegacyChanged(false);
  }, [assignment]);

  const addFiles = (selected) => {
    const valid = [];
    selected.forEach((file) => {
      const error = assignmentFileError(file);
      if (error) toast.error(error); else valid.push(file);
    });
    setNewFiles((current) => [...current, ...valid].slice(0, 5));
  };

  const removeExisting = (file, index) => {
    setAttachments((current) => current.filter((_, itemIndex) => itemIndex !== index));
    if (file.file_id) setRemovedFileIds((current) => [...current, file.file_id]);
    else setLegacyChanged(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      fields.forEach((field) => {
        if ((form[field] ?? "") !== (assignment[field] ?? "")) payload[field] = form[field];
      });
      if (legacyChanged) payload.attachments = attachments;
      if (Object.keys(payload).length) await api.patch(`/assignments/${assignment.id}`, payload);
      for (const fileId of removedFileIds) await api.delete(`/assignments/${assignment.id}/files/${fileId}`);
      if (removeAnswerKey && assignment.answer_key_file?.file_id && !answerKeyFile) {
        await api.delete(`/assignments/${assignment.id}/files/${assignment.answer_key_file.file_id}`);
      }
      if (newFiles.length) await uploadAssignmentFiles(assignment.id, "attachment", newFiles);
      if (answerKeyFile) await uploadAssignmentFiles(assignment.id, "answer_key", [answerKeyFile]);
      toast.success("Assignment updated");
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not update assignment");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={Boolean(assignment)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="assignment-edit-dialog" closeTestId="assignment-edit-dialog-close" className="max-h-[90vh] overflow-y-auto rounded-2xl sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit assignment</DialogTitle>
          <DialogDescription>Only changed fields and explicitly removed files will be updated.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4">
          <label className="sm:col-span-2 text-sm font-medium">Title
            <input data-testid="edit-assignment-title" value={form.title || ""} onChange={(e)=>setForm({...form, title:e.target.value})}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200" />
          </label>
          <label className="text-sm font-medium">Subject
            <select data-testid="edit-assignment-subject" value={form.subject || ""} onChange={(e)=>setForm({...form, subject:e.target.value})}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200 bg-white">
              {subjects.map((subject) => <option key={subject.slug} value={subject.slug}>{subject.title}</option>)}
            </select>
          </label>
          <label className="text-sm font-medium">Due date
            <input data-testid="edit-assignment-due" type="date" value={form.due_date || ""} onChange={(e)=>setForm({...form, due_date:e.target.value})}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200" />
          </label>
          <label className="sm:col-span-2 text-sm font-medium">Instructions / description
            <textarea data-testid="edit-assignment-description" rows={4} value={form.description || ""} onChange={(e)=>setForm({...form, description:e.target.value})}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200" />
          </label>
          <label className="sm:col-span-2 text-sm font-medium">Video URL
            <input data-testid="edit-assignment-video" type="url" value={form.video_url || ""} onChange={(e)=>setForm({...form, video_url:e.target.value})}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-slate-200" />
          </label>

          <div className="sm:col-span-2 space-y-2">
            <div className="text-sm font-medium">Student-visible attachments</div>
            {attachments.map((file, index) => (
              <div key={file.file_id || `${file.file_name}-${index}`} className="flex items-center justify-between gap-3 px-3 py-2 rounded-xl bg-slate-50 border border-slate-200">
                <AssignmentFileLink file={file} testId={`edit-attachment-download-${index}`} />
                <button type="button" data-testid={`edit-attachment-remove-${index}`} onClick={()=>removeExisting(file, index)} aria-label={`Remove ${file.file_name}`} className="text-slate-500 hover:text-red-700"><X size={15} /></button>
              </div>
            ))}
            <label className="flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-slate-300 cursor-pointer text-sm text-slate-600">
              <Upload size={16} /> Add files (10 MB each)
              <input data-testid="edit-assignment-files" type="file" multiple accept={ASSIGNMENT_FILE_ACCEPT} className="hidden" onChange={(e)=>addFiles(Array.from(e.target.files || []))} />
            </label>
            {newFiles.map((file, index) => <div key={`${file.name}-${index}`} className="text-xs text-slate-600">New: {file.name}</div>)}
          </div>

          <div className="sm:col-span-2 p-4 rounded-xl bg-amber-50 border border-amber-200 space-y-3" data-testid="edit-answer-key-section">
            <div className="flex items-center gap-2 font-semibold text-amber-900"><KeyRound size={16} /> Parent Answer Key</div>
            <textarea data-testid="edit-answer-key-text" rows={4} value={form.answer_key_text || ""} onChange={(e)=>setForm({...form, answer_key_text:e.target.value})}
              placeholder="Typed answer key (parent only)" className="w-full px-4 py-3 rounded-xl border border-amber-200 bg-white" />
            {assignment?.answer_key_file && !removeAnswerKey && !answerKeyFile && (
              <div className="flex items-center justify-between gap-3">
                <AssignmentFileLink file={assignment.answer_key_file} testId="edit-answer-key-download" />
                <button type="button" data-testid="edit-answer-key-remove" onClick={()=>setRemoveAnswerKey(true)} className="text-xs font-semibold text-red-700">Remove file</button>
              </div>
            )}
            <label className="flex items-center gap-2 px-4 py-3 rounded-xl border border-dashed border-amber-300 bg-white cursor-pointer text-sm text-amber-900">
              <Upload size={16} /> {answerKeyFile ? answerKeyFile.name : "Add or replace answer-key file"}
              <input data-testid="edit-answer-key-file" type="file" accept={ASSIGNMENT_FILE_ACCEPT} className="hidden" onChange={(e)=>{
                const file=e.target.files?.[0]; if (!file) return; const error=assignmentFileError(file); if (error) toast.error(error); else setAnswerKeyFile(file);
              }} />
            </label>
          </div>
        </div>
        <DialogFooter>
          <button type="button" data-testid="edit-assignment-cancel" onClick={onClose} className="px-5 py-2.5 rounded-full border border-slate-300 font-semibold">Cancel</button>
          <button type="button" data-testid="edit-assignment-save" onClick={save} disabled={saving || !form.title?.trim()}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-teal-700 hover:bg-teal-800 text-white font-semibold disabled:opacity-50">
            <Save size={15} /> {saving ? "Saving…" : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};